import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";

function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is already logged in, redirect to dashboard
        navigate("/doctor", { replace: true });
      } else {
        setCheckingAuth(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Show loading while checking auth state
  if (checkingAuth) {
    return (
      <div className="login-layout">
        <div className="login-content" style={{ textAlign: "center" }}>
          <div
            className="loading-spinner"
            style={{ margin: "0 auto 16px" }}
          ></div>
          <p style={{ color: "#64748b" }}>Checking authentication...</p>
        </div>
      </div>
    );
  }

  const toFriendlyError = (err) => {
    const code = err?.code || "";
    switch (code) {
      case "auth/operation-not-allowed":
        return "Email/Password sign-in is disabled. Enable it in Firebase Console → Authentication → Sign-in method.";
      case "auth/invalid-api-key":
        return "Invalid Firebase API key. Check your .env values match the Firebase project.";
      case "auth/domain-not-allowed":
        return "This domain is not authorized for Firebase Auth. Add your dev domain (e.g., localhost) in Authentication → Settings → Authorized domains.";
      case "auth/network-request-failed":
        return "Network error. Check your connection and try again.";
      case "auth/wrong-password":
      case "auth/invalid-credential":
        return "Incorrect email or password.";
      case "auth/user-not-found":
        return "No account found for that email.";
      default:
        return err?.message || "Login failed. Please try again.";
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    try {
      setLoading(true);
      await signInWithEmailAndPassword(auth, email, password);
      setSuccess("Logged in successfully.");
      navigate("/doctor", { replace: true });
    } catch (err) {
      setError(toFriendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError("");
    setSuccess("");
    try {
      setLoading(true);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if user doc exists, if not create one
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        await setDoc(userRef, {
          fullName: user.displayName || "Doctor",
          email: user.email,
          role: "doctor",
          createdAt: serverTimestamp(),
        });
      }

      navigate("/doctor", { replace: true });
    } catch (err) {
      setError(toFriendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-layout">
      <div className="login-content">
        <div className="login-logo">
          <img src="/logo.png" alt="PhysioKea" className="login-logo-image" />
        </div>

        <div className="login-heading">
          <h2>Welcome back, Doctor</h2>
          <p>Log in to manage your appointments</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <label className="login-field">
            <span className="login-label">Email Address</span>
            <div className="login-input">
              <span className="material-symbols-outlined">mail</span>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="physio@example.com"
                required
              />
            </div>
          </label>

          <label className="login-field">
            <div className="login-label-row">
              <span className="login-label">Password</span>
              <button
                type="button"
                className="login-link"
                onClick={() => navigate("/login")}
              >
                Forgot Password?
              </button>
            </div>
            <div className="login-input">
              <span className="material-symbols-outlined">lock</span>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                className="login-input-btn"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((prev) => !prev)}
              >
                <span className="material-symbols-outlined">
                  {showPassword ? "visibility" : "visibility_off"}
                </span>
              </button>
            </div>
          </label>

          {error && <p className="feedback error login-feedback">{error}</p>}
          {success && (
            <p className="feedback success login-feedback">{success}</p>
          )}

          <button type="submit" className="login-cta" disabled={loading}>
            {loading ? (
              <>
                <div className="loading-spinner" />
                <span>Logging in...</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined">login</span>
                <span>Log in</span>
              </>
            )}
          </button>

          <div className="login-divider">
            <span>Or login with</span>
          </div>

          <button
            type="button"
            className="login-alt"
            onClick={handleGoogleLogin}
            disabled={loading}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 48 48"
              style={{ marginRight: 8 }}
            >
              <path
                fill="#EA4335"
                d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
              />
              <path
                fill="#4285F4"
                d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
              />
              <path
                fill="#FBBC05"
                d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
              />
              <path
                fill="#34A853"
                d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
              />
            </svg>
            <span>Continue with Google</span>
          </button>
        </form>

        {/* <p className="login-footer">
          New to PhysioKea? <Link to="/register">Sign Up</Link>
        </p> */}
      </div>

      <div className="login-decor">
        <div className="login-blob login-blob-a" />
        <div className="login-blob login-blob-b" />
      </div>
    </div>
  );
}

export default LoginPage;
